import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronRight,
  Check,
  Search,
  Users,
  ClipboardList,
  FileText,
  PowerOff,
  Code,
  Database,
  Plus,
  X,
  Globe,
} from "lucide-react";
import AssessmentClient, { AssessmentEntity } from "../data/api/AssessmentClient";
import LearnerClient from "../../learnerControl/data/api/LearnerClient";
import Loader from "../../../shared/components/Loader";
import Alert from "../../../shared/components/Alert";
import Modal from "../../../shared/components/Modal";
import DateTimePicker from "../../../shared/components/DateTimePicker";
import DateTimeRangePicker from "../../../shared/components/DateTimeRangePicker";
import TimezoneSelect from "react-timezone-select";
import { GlobeTimezoneModal } from "../../../shared/components/GlobeTimezoneModal";
import { zonedLocalToUtcDate, utcDateToZonedLocal, zonedLocalToUtcIsoDate } from "../../interviewSchedule/utils/timezoneUtils";
import { DifficultyPopover, ScoringPopover, LanguageSelectPopover } from "../components/ConfigPopovers";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "../../../shared/components/Table/Table";
import "../styles/AssessmentStyles.css";

const MAX_JOZUNA_QUESTIONS = 60;

const autoDistributeDifficulty = (count: number, maxAvailable?: { easy: number; medium: number; hard: number }) => {
  const total = Math.max(0, count || 0);
  if (total === 0) return { easy: 0, medium: 0, hard: 0 };
  
  if (!maxAvailable) {
    const base = Math.floor(total / 3);
    const remainder = total % 3;
    return {
      easy: base + (remainder >= 1 ? 1 : 0),
      medium: base + (remainder >= 2 ? 1 : 0),
      hard: base,
    };
  }

  let remaining = total;
  const result = { easy: 0, medium: 0, hard: 0 };
  
  while (remaining > 0) {
    let allocated = 0;
    if (remaining > 0 && result.easy < maxAvailable.easy) { result.easy++; remaining--; allocated++; }
    if (remaining > 0 && result.medium < maxAvailable.medium) { result.medium++; remaining--; allocated++; }
    if (remaining > 0 && result.hard < maxAvailable.hard) { result.hard++; remaining--; allocated++; }
    if (allocated === 0) break;
  }
  
  return result;
};

const EditAssessmentPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState<string>("Upcoming");
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    title: "",
    message: "",
    type: "success" as "success" | "error",
  });

  const [users, setUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [searchUser, setSearchUser] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pageSize = 20;

  const [form, setForm] = useState({
    assessmentName: "",
    assessmentOtp: "",
    duration: 30,
    startTime: "",
    endTime: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    programmingConfigs: [] as any[],
    industryConfigs: [] as any[],
    selectedDomains: [] as string[],
    questionCount: 12,
    descriptiveCount: 0,
  });

  const [programmingDomains, setProgrammingDomains] = useState<any[]>([]);
  const [sqlDomains, setSqlDomains] = useState<any[]>([]);
  const [industryModules, setIndustryModules] = useState<any[]>([]);

  const [moduleStats, setModuleStats] = useState<Record<string, { total: number, easy: number, medium: number, hard: number, loading: boolean }>>({});

  const fetchModuleStats = async (domainType: 'programming' | 'industry', moduleId: string) => {
    if (!moduleId || moduleStats[moduleId]) return;
    setModuleStats((prev) => ({ ...prev, [moduleId]: { total: 0, easy: 0, medium: 0, hard: 0, loading: true } }));
    try {
      const stats = await AssessmentClient.getModuleStats(domainType, moduleId);
      setModuleStats((prev) => ({ ...prev, [moduleId]: { ...stats, loading: false } }));
    } catch (err) {
      console.error("Failed to fetch module stats", err);
      setModuleStats((prev) => ({ ...prev, [moduleId]: { total: 0, easy: 0, medium: 0, hard: 0, loading: false } }));
    }
  };

  const isClosed = (status || "").toLowerCase() === "closed";
  const isActive = (status || "").toLowerCase() === "active";
  const isUpcoming = (status || "").toLowerCase() === "upcoming" || !status;

  const isFieldDisabled = isActive || isClosed;
  const isAllDisabled = isActive || isClosed;
  const isEndTimeEditable = !isClosed;
  const [isGlobeModalOpen, setIsGlobeModalOpen] = useState(false);

  const [closeAlert, setCloseAlert] = useState(false);
  const [closing, setClosing] = useState(false);

  const handleCloseConfirm = async () => {
    if (!id) return;
    setClosing(true);
    try {
      await AssessmentClient.closeAssessment(id);
      setAlert({ show: true, title: "Success", message: "Assessment closed successfully.", type: "success" });
      setStatus("Closed");
    } catch {
      setAlert({ show: true, title: "Error", message: "Failed to close the assessment.", type: "error" });
    } finally {
      setClosing(false);
      setCloseAlert(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    const loadData = async () => {
      setLoading(true);
      try {
        const assessment = await AssessmentClient.getAssessmentById(id);

        setStatus(assessment.status || "Upcoming");

        // Safely extract candidate IDs
        const rawCandidates = assessment.target_candidates || [];
        const candidateIds = rawCandidates.map((c: any) =>
          typeof c === "string" ? c : c.candidate_id || c.email_address || String(c)
        ).filter(Boolean);
        setSelectedUsers(candidateIds);

        // Correctly populate selectedDomains from assessment response
        const loadedDomains: string[] = [];
        if (
          assessment.dimension_questions ||
          (assessment.number_of_dimension_questions || 0) > 0
        ) {
          loadedDomains.push("JOZUNA");
        }

        const progConfigs = Array.isArray(assessment.programming_configs)
          ? assessment.programming_configs
          : [];
        if (progConfigs.length > 0) {
          loadedDomains.push("Programming");
        }

        const indConfigs = Array.isArray(assessment.industry_configs)
          ? assessment.industry_configs
          : [];
        if (indConfigs.length > 0) {
          loadedDomains.push("Industry");
        }

        const formatIsoForPicker = (raw?: string, tz?: string) => {
          if (!raw) return "";
          try {
            return utcDateToZonedLocal(raw, tz || Intl.DateTimeFormat().resolvedOptions().timeZone);
          } catch {
            return "";
          }
        };

        setForm({
          assessmentName: assessment.assessment_name || "",
          assessmentOtp: String(assessment.assessment_otp || ""),
          duration: assessment.assessment_duration || 30,
          startTime: formatIsoForPicker(assessment.assessment_start_time, assessment.timezone),
          endTime: formatIsoForPicker(assessment.assessment_end_time, assessment.timezone),
          timezone:
            assessment.timezone ||
            Intl.DateTimeFormat().resolvedOptions().timeZone,
          questionCount: assessment.number_of_dimension_questions || 12,
          descriptiveCount: assessment.dimension_descriptive_questions || 0,
          programmingConfigs: progConfigs,
          industryConfigs: indConfigs,
          selectedDomains: loadedDomains,
        });

        progConfigs.forEach((c: any) => {
          if (c.module_id) fetchModuleStats('programming', String(c.module_id));
        });
        indConfigs.forEach((c: any) => {
          if (c.module_id) fetchModuleStats('industry', String(c.module_id));
        });
      } catch (err: any) {
        setAlert({
          show: true,
          title: "Error",
          message: err.message || "Failed to load assessment",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  useEffect(() => {
    loadUsers(1, true);

    const fetchDomains = async () => {
      try {
        const pDomains: any = await AssessmentClient.getDomains("Programming");
        setProgrammingDomains(pDomains?.domains || pDomains || []);
        const sDomains: any = await AssessmentClient.getDomains("SQL");
        setSqlDomains(sDomains?.domains || sDomains || []);
        const iModules: any = await AssessmentClient.getDomains("Industry");
        setIndustryModules(iModules?.modules || iModules || []);
      } catch (err) {
        console.error("Failed to load domains", err);
      }
    };
    fetchDomains();
  }, []);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      loadUsers(1, true);
    }, 300);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchUser]);

  const loadUsers = async (page: number, reset: boolean) => {
    if (reset) setFetchingUsers(true);
    else setLoadingMore(true);
    try {
      const params: any = {
        page_number: page,
        limit: pageSize,
        status: "Active",
      };
      if (searchUser.trim()) params.name = searchUser.trim();
      const response = await LearnerClient.list(params);
      const data = response?.data || response;
      const records = Array.isArray(data) ? data : data?.records || [];
      const total = data?.total_no_of_records || records.length;
      if (reset) setUsers(records);
      else setUsers((prev) => [...prev, ...records]);
      setTotalRecords(total);
      setPageNumber(page);
      setHasMore(records.length === pageSize);
    } catch (err) {
      console.error("Failed to load users", err);
    } finally {
      setFetchingUsers(false);
      setLoadingMore(false);
    }
  };

  const toggleUser = (id: string) => {
    if (isFieldDisabled) return;
    setSelectedUsers((prev) =>
      prev.includes(id)
        ? prev.filter((e) => e !== id)
        : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (isFieldDisabled) return;
    const allEmails = users.map((u) => u.candidate_id).filter(Boolean);
    const allSelected = allEmails.every((e) => selectedUsers.includes(e));
    setSelectedUsers(allSelected ? [] : allEmails);
  };

  const generateOtp = () => {
    const code = Math.floor(1000 + Math.random() * 9000).toString();
    setForm((prev) => ({ ...prev, assessmentOtp: code }));
  };

  const handleSubmit = async () => {
    if (!id) return;
    setSaving(true);
    try {
      if (!form.assessmentName.trim())
        throw new Error("Assessment name is required");

      if (!isActive) {
        if (!form.startTime) throw new Error("Start time is required");
        if (!form.endTime) throw new Error("End time is required");
        const startDate = new Date(form.startTime);
        const endDate = new Date(form.endTime);
        if (endDate <= startDate)
          throw new Error("End time must be after start time");
      } else {
        if (!form.endTime) throw new Error("End time is required");
        if (form.startTime && form.endTime) {
          const startDate = new Date(form.startTime);
          const endDate = new Date(form.endTime);
          if (endDate <= startDate)
            throw new Error("End time must be after start time");
        }
      }

      const payload: any = {
        assessment_name: form.assessmentName.trim(),
        assessment_duration: form.duration,
        assessment_otp: form.assessmentOtp || undefined,
        assessment_start_time: form.startTime ? zonedLocalToUtcIsoDate(form.startTime, form.timezone) : undefined,
        assessment_end_time: form.endTime ? zonedLocalToUtcIsoDate(form.endTime, form.timezone) : undefined,
        timezone: form.timezone,
        dimension_questions: form.selectedDomains.includes('JOZUNA'),
        number_of_dimension_questions: form.selectedDomains.includes('JOZUNA') ? form.questionCount : 0,
        dimension_descriptive_questions: form.selectedDomains.includes('JOZUNA') ? form.descriptiveCount : 0,
        dimension_list: [],
        target_candidates: selectedUsers,
        programming_configs: (form.selectedDomains.includes('Programming') && form.programmingConfigs.length > 0) ? form.programmingConfigs : undefined,
        industry_configs: (form.selectedDomains.includes('Industry') && form.industryConfigs.length > 0) ? form.industryConfigs : undefined,
        status: status,
      };

      await AssessmentClient.updateAssessment(id, payload);
      setAlert({
        show: true,
        title: "Success",
        message: "Assessment updated successfully!",
        type: "success",
      });
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || err.message || "Failed to update assessment";
      setAlert({
        show: true,
        title: "Error",
        message: errorMsg,
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateForm = (patch: Partial<typeof form>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  return (
    <motion.div
      className="learner-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
    >
      <Loader
        isLoading={loading || saving}
        message={saving ? "Saving changes..." : "Loading assessment..."}
      />
      <Alert
        show={alert.show}
        title={alert.title}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert((prev) => ({ ...prev, show: false }))}
        primaryButtonText="OK"
        onPrimaryClick={() => {
          setAlert((prev) => ({ ...prev, show: false }));
          if (alert.type === "success") navigate("/dashboard/assessments");
        }}
      />

      <div className="lp-hero" style={{ marginBottom: "24px" }}>
        <div className="lp-hero-left" style={{ alignItems: "flex-start" }}>
          <button
            className="btn-ghost is-back-btn"
            onClick={() => navigate("/dashboard/assessments")}
            style={{ padding: "8px", height: "auto", borderRadius: "10px" }}
          >
            <ArrowLeft size={20} />
          </button>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: "16px",
              paddingTop: "2px",
            }}
          >
            <h1 style={{ margin: 0 }}>Edit Assessment</h1>
            <span
              className="lp-hero-chip"
              style={{ display: "inline-flex" }}
            >
              Modify your assessment configuration
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {isActive && (
            <button
              className="btn-danger"
              style={{ padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', backgroundColor: '#fff7ed', color: '#ea580c', border: '1px solid #ffedd5' }}
              onClick={() => setCloseAlert(true)}
            >
              <PowerOff size={16} /> Close Assessment
            </button>
          )}
          <span
            style={{
              padding: "6px 12px",
              borderRadius: "12px",
              fontSize: "14px",
              fontWeight: 600,
              backgroundColor: isActive
                ? "#dcfce7"
                : isClosed
                  ? "#f1f5f9"
                  : "#e0e7ff",
              color: isActive
                ? "#166534"
                : isClosed
                  ? "#475569"
                  : "#3730a3",
            }}
          >
            STATUS: {status.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="is-split-layout">
        <div className="is-sidebar-steps">
          <div
            className={`is-sidebar-step ${step === 1 ? "active" : step > 1 ? "completed" : ""
              }`}
            onClick={() => !isClosed && setStep(1)}
            style={{ cursor: isClosed ? "not-allowed" : "pointer" }}
          >
            <div className="is-step-icon">
              {step > 1 ? <Check size={16} /> : 1}
            </div>
            <div className="is-step-text">
              <strong>Assessment Details</strong>
              <span>Configure basic info</span>
            </div>
          </div>
          <div className="is-step-connector" />
          <div
            className={`is-sidebar-step ${step === 2 ? "active" : step > 2 ? "completed" : ""
              }`}
            onClick={() => !isClosed && setStep(2)}
            style={{ cursor: isClosed ? "not-allowed" : "pointer" }}
          >
            <div className="is-step-icon">
              {step > 2 ? <Check size={16} /> : 2}
            </div>
            <div className="is-step-text">
              <strong>Question Configuration</strong>
              <span>Question count &amp; types</span>
            </div>
          </div>
          <div className="is-step-connector" />
          <div
            className={`is-sidebar-step ${step === 3 ? "active" : ""}`}
            onClick={() => !isClosed && setStep(3)}
            style={{ cursor: isClosed ? "not-allowed" : "pointer" }}
          >
            <div className="is-step-icon">3</div>
            <div className="is-step-text">
              <strong>Participants</strong>
              <span>Assign candidates</span>
            </div>
          </div>
        </div>

        <div className="is-main-content">
          {step === 1 && (
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <div className="is-form-scrollable" style={{ paddingRight: "8px", paddingBottom: "24px", display: "flex", flexDirection: "column", gap: "20px" }}>
                <div className="is-form-card">
                  <div className="is-human-panel-header" style={{ marginBottom: "16px" }}>
                    <div className="is-human-panel-icon" style={{ background: "#F3EBFC", color: "#7A3FC2" }}><ClipboardList size={16} /></div>
                    <h3 style={{ margin: 0 }}>Assessment Details</h3>
                  </div>

                  <div className="is-form-row">
                    <div className="is-form-group">
                      <label style={{ color: isFieldDisabled ? "#94a3b8" : undefined }}>Assessment Name <span style={{ color: "#dc2626" }}>*</span></label>
                      <input type="text" disabled={isFieldDisabled} value={form.assessmentName} onChange={(e) => updateForm({ assessmentName: e.target.value })} />
                    </div>
                    <div className="is-form-group">
                      <label>Assessment OTP</label>
                      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <input type="text" disabled={isFieldDisabled} value={form.assessmentOtp} onChange={(e) => updateForm({ assessmentOtp: e.target.value })} />
                        {!isFieldDisabled && <button type="button" className="btn-primary" onClick={generateOtp}>Generate</button>}
                      </div>
                    </div>
                  </div>

                  <div className="is-form-row">
                    <div className="is-form-group">
                      <label style={{ color: isAllDisabled ? "#94a3b8" : undefined }}>Duration (minutes) <span style={{ color: "#dc2626" }}>*</span></label>
                      <input type="number" disabled={isAllDisabled} value={form.duration} onChange={(e) => {
                        const duration = Number(e.target.value);
                        const update: any = { duration };
                        if (form.startTime) {
                          const d = new Date(form.startTime);
                          const endD = new Date(d.getTime() + duration * 60000);
                          update.endTime = new Date(endD.getTime() - endD.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                        }
                        updateForm(update);
                      }} />
                    </div>
                    <div className="is-form-group">
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <label style={{ margin: 0, color: isFieldDisabled ? "#94a3b8" : undefined }}>Timezone <span style={{ color: "#dc2626" }}>*</span></label>
                        <button
                          type="button"
                          disabled={isFieldDisabled}
                          onClick={() => setIsGlobeModalOpen(true)}
                          style={{
                            background: "none",
                            border: "none",
                            color: isFieldDisabled ? "#94a3b8" : "var(--primary)",
                            cursor: isFieldDisabled ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            padding: 0,
                          }}
                        >
                          <Globe size={14} /> Map
                        </button>
                      </div>
                      <TimezoneSelect
                        value={form.timezone}
                        onChange={(tz: any) =>
                          updateForm({ timezone: tz.value })
                        }
                        isDisabled={isFieldDisabled}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            minHeight: "40px",
                            height: "40px",
                            borderRadius: "8px",
                            borderColor: state.isFocused
                              ? "var(--primary)"
                              : "var(--border)",
                            boxShadow: state.isFocused
                              ? "0 0 0 1px var(--primary)"
                              : "none",
                            "&:hover": {
                              borderColor: "var(--border-hover)",
                            },
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? "var(--primary)"
                              : state.isFocused
                                ? "var(--primary-light)"
                                : "transparent",
                            color: state.isSelected ? "white" : "inherit",
                            "&:hover": {
                              backgroundColor: state.isSelected
                                ? "var(--primary)"
                                : "var(--primary-light)",
                            },
                          }),
                        }}
                      />
                    </div>
                  </div>

                  <div className="is-form-row">
                    <div className="is-form-group" style={{ gridColumn: "1 / -1" }}>
                      <label style={{ color: isFieldDisabled ? "#94a3b8" : undefined }}>Assessment Schedule <span style={{ color: "#dc2626" }}>*</span></label>
                      <DateTimeRangePicker
                        startTime={form.startTime}
                        endTime={form.endTime}
                        duration={form.duration}
                        onChange={(start, end) => updateForm({ startTime: start, endTime: end })}
                        disabled={isClosed}
                        disableStart={isActive}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="is-form-actions" style={{ justifyContent: "flex-start", gap: "12px", paddingTop: "16px", borderTop: "1px solid var(--border)" }}>
                <button className="btn-ghost" onClick={() => navigate("/dashboard/assessments")} type="button">Cancel</button>
                <button className="btn-primary" onClick={() => setStep(2)} type="button" disabled={isClosed}>Next <ChevronRight size={16} /></button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <div className="is-form-scrollable" style={{ paddingRight: "8px", paddingBottom: "24px", display: "flex", flexDirection: "column", gap: "20px" }}>
                <div className="is-form-card" style={{ flex: "none", height: "auto", minHeight: 0, marginBottom: "16px" }}>
                  <div
                    className="is-human-panel-header"
                    style={{
                      marginBottom: form.selectedDomains.includes('JOZUNA') ? "16px" : "0",
                      borderBottom: "none",
                      paddingBottom: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      cursor: isFieldDisabled ? 'not-allowed' : 'pointer'
                    }}
                    onClick={() => { 
                      if (!isFieldDisabled) {
                        const isAdding = !form.selectedDomains.includes('JOZUNA');
                        updateForm({ 
                          selectedDomains: isAdding ? [...form.selectedDomains, 'JOZUNA'] : form.selectedDomains.filter(d => d !== 'JOZUNA'),
                          ...(isAdding ? {} : { questionCount: 0, descriptiveCount: 0 })
                        }); 
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.selectedDomains.includes('JOZUNA')}
                      disabled={isFieldDisabled}
                      onChange={() => { }}
                      style={{ width: '20px', height: '20px', cursor: isFieldDisabled ? 'not-allowed' : 'pointer', accentColor: 'var(--primary)', flexShrink: 0, opacity: isFieldDisabled ? 0.6 : 1 }}
                    />
                    <div className="is-human-panel-icon" style={{ background: "#FEF3C7", color: "#D97706" }}><FileText size={16} /></div>
                    <h3 style={{ margin: 0 }}> JozHRM Soft Skill</h3>
                  </div>

                  {form.selectedDomains.includes('JOZUNA') && (
                    <>
                      <div className="is-form-row">
                        <div className="is-form-group">
                          <label style={{ color: isFieldDisabled ? "#94a3b8" : undefined }}>Objective Questions <span style={{ color: "#dc2626" }}>*</span></label>
                            <input
                              type="text"
                              inputMode="numeric"
                              disabled={isFieldDisabled}
                              value={form.questionCount}
                              onChange={(e) => {
                                let val = Math.min(MAX_JOZUNA_QUESTIONS, Number(e.target.value.replace(/\D/g, "")) || 0);
                                if (val + form.descriptiveCount > MAX_JOZUNA_QUESTIONS) {
                                  val = MAX_JOZUNA_QUESTIONS - form.descriptiveCount;
                                }
                                updateForm({ questionCount: val });
                              }}
                            />
                        </div>
                      </div>
                      <div style={{ padding: "12px 16px", background: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0", color: "#166534" }}>
                        <span>Total Questions: {form.questionCount + form.descriptiveCount}</span>
                      </div>
                    </>
                  )}
                </div>

                <div className="is-form-card" style={{ flex: "none", height: "auto", minHeight: 0, marginBottom: "16px" }}>
                  <div
                    className="is-human-panel-header"
                    style={{
                      marginBottom: form.selectedDomains.includes('Programming') ? "16px" : "0",
                      borderBottom: "none",
                      paddingBottom: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      cursor: isFieldDisabled ? 'not-allowed' : 'pointer'
                    }}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button')) return;
                      if (isFieldDisabled) return;
                      const isAdding = !form.selectedDomains.includes('Programming');
                      const newDomains = isAdding
                        ? [...form.selectedDomains, 'Programming']
                        : form.selectedDomains.filter(d => d !== 'Programming');
                      const newProgrammingConfigs = (isAdding && form.programmingConfigs.length === 0)
                        ? [{ module_id: '', domain_type: 'Programming', question_count: 0, difficulty_distribution: { easy: 0, medium: 0, hard: 0 }, programming_languages: [] }]
                        : (isAdding ? form.programmingConfigs : []);
                      updateForm({ selectedDomains: newDomains, programmingConfigs: newProgrammingConfigs });
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.selectedDomains.includes('Programming')}
                      disabled={isFieldDisabled}
                      onChange={() => { }}
                      style={{ width: '20px', height: '20px', cursor: isFieldDisabled ? 'not-allowed' : 'pointer', accentColor: 'var(--primary)', flexShrink: 0, opacity: isFieldDisabled ? 0.6 : 1 }}
                    />
                    <div className="is-human-panel-icon" style={{ background: "#E0E7FF", color: "#4F46E5" }}><Code size={16} /></div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", width: "100%", justifyContent: "space-between" }}>
                      <h3 style={{ margin: 0 }}> Programming</h3>
                    </div>
                  </div>

                  {form.selectedDomains.includes('Programming') && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                      {programmingDomains.map((domain) => {
                        const isSelected = form.programmingConfigs.some(c => String(c.module_id) === String(domain.domain_id));
                        const configIdx = form.programmingConfigs.findIndex(c => String(c.module_id) === String(domain.domain_id));
                        const config = configIdx !== -1 ? form.programmingConfigs[configIdx] : null;

                        return (
                          <div key={domain.domain_id} className="is-form-row" style={{ alignItems: "center", background: "#ffffff", padding: "16px 20px", borderRadius: "12px", border: isSelected ? "1px solid #c7d2fe" : "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", display: "flex", flexWrap: "wrap", gap: "24px" }}>
                            {isSelected && moduleStats[String(domain.domain_id)] && (
                              <div style={{ flex: "1 1 100%", marginBottom: "12px" }}>
                                <div style={{ display: "inline-flex", fontSize: "14px", color: "#64748b", background: "#f8fafc", padding: "10px 14px", borderRadius: "8px", gap: "20px", alignItems: "center" }}>
                                  {moduleStats[String(domain.domain_id)].loading ? (
                                    <span>Loading stats...</span>
                                  ) : (
                                  <>
                                    <span style={{ fontWeight: 600, color: "#334155" }}>Available Questions:</span>
                                    <span><span style={{ color: "#22c55e", fontWeight: 600 }}>Easy:</span> {moduleStats[String(domain.domain_id)].easy}</span>
                                    <span><span style={{ color: "#eab308", fontWeight: 600 }}>Medium:</span> {moduleStats[String(domain.domain_id)].medium}</span>
                                    <span><span style={{ color: "#ef4444", fontWeight: 600 }}>Hard:</span> {moduleStats[String(domain.domain_id)].hard}</span>
                                    <span><span style={{ color: "#6366f1", fontWeight: 600 }}>Total:</span> {moduleStats[String(domain.domain_id)].total}</span>
                                  </>
                                )}
                                </div>
                              </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '0 0 auto', marginBottom: 0 }}>
                              <input 
                                type="checkbox" 
                                checked={isSelected}
                                disabled={isFieldDisabled}
                                onChange={(e) => {
                                  if (isFieldDisabled) return;
                                  if (e.target.checked) {
                                    updateForm({
                                      programmingConfigs: [...form.programmingConfigs, {
                                        module_id: String(domain.domain_id),
                                        domain_type: 'Programming',
                                        question_count: 0,
                                        difficulty_distribution: { easy: 0, medium: 0, hard: 0 },
                                        programming_languages: []
                                      }]
                                    });
                                    fetchModuleStats('programming', String(domain.domain_id));
                                  } else {
                                    updateForm({
                                      programmingConfigs: form.programmingConfigs.filter(c => String(c.module_id) !== String(domain.domain_id))
                                    });
                                  }
                                }}
                                style={{ width: '18px', height: '18px', cursor: isFieldDisabled ? 'not-allowed' : 'pointer', accentColor: 'var(--primary)', opacity: isFieldDisabled ? 0.6 : 1 }}
                              />
                              <span style={{ fontSize: '15px', fontWeight: 600, color: '#334155' }}>{domain.domain_name}</span>
                            </div>

                            {isSelected && config && (
                              <>
                                {((domain.domain_name || "").toLowerCase().includes("competitive")) && (
                                  <div className="is-form-group" style={{ flex: "0 0 auto", marginBottom: 0 }}>
                                    <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px", display: "block" }}>Languages</label>
                                    <LanguageSelectPopover
                                      disabled={isFieldDisabled}
                                      selectedLanguages={config.programming_languages || []}
                                      onChange={(langs) => {
                                        const newConfigs = [...form.programmingConfigs];
                                        newConfigs[configIdx].programming_languages = langs;
                                        updateForm({ programmingConfigs: newConfigs });
                                      }}
                                    />
                                  </div>
                                )}
                                <div className="is-form-group" style={{ flex: "0 0 100px", width: "100px", marginBottom: 0 }}>
                                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px", display: "block" }}>Questions</label>
                                  <input
                                    style={{ height: "36px", padding: "0 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", outline: "none", width: "100%", textAlign: "center", fontWeight: 700 }}
                                    type="text"
                                    inputMode="numeric"
                                    value={config.question_count}
                                    disabled={isFieldDisabled}
                                    onChange={(e) => {
                                      const raw = e.target.value.replace(/\D/g, "");
                                      const stats = moduleStats[String(domain.domain_id)];
                                      const maxAllowed = stats && !stats.loading ? stats.total : 9999;
                                      const newCount = Math.min(maxAllowed, Number(raw) || 0);
                                      const newConfigs = [...form.programmingConfigs];
                                      newConfigs[configIdx].question_count = newCount;
                                      newConfigs[configIdx].difficulty_distribution = autoDistributeDifficulty(newCount, stats && !stats.loading ? stats : undefined);
                                      updateForm({ programmingConfigs: newConfigs });
                                    }}
                                  />
                                </div>
                                <div className="is-form-group" style={{ flex: "0 0 auto", marginBottom: 0 }}>
                                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px", display: "block" }}>Difficulty</label>
                                  <DifficultyPopover 
                                    disabled={isFieldDisabled} 
                                    distribution={config.difficulty_distribution as any} 
                                    maxQuestions={config.question_count} 
                                    maxAvailable={moduleStats[String(domain.domain_id)] && !moduleStats[String(domain.domain_id)].loading ? { easy: moduleStats[String(domain.domain_id)].easy, medium: moduleStats[String(domain.domain_id)].medium, hard: moduleStats[String(domain.domain_id)].hard } : undefined}
                                    onChange={(dist: any) => {
                                      const newConfigs = [...form.programmingConfigs];
                                      newConfigs[configIdx].difficulty_distribution = dist;
                                      updateForm({ programmingConfigs: newConfigs });
                                    }} 
                                  />
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="is-form-card" style={{ flex: "none", height: "auto", minHeight: 0, marginBottom: "16px" }}>
                  <div
                    className="is-human-panel-header"
                    style={{
                      marginBottom: form.selectedDomains.includes('Industry') ? "16px" : "0",
                      borderBottom: "none",
                      paddingBottom: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      cursor: isFieldDisabled ? 'not-allowed' : 'pointer'
                    }}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest('button')) return;
                      if (isFieldDisabled) return;
                      const isAdding = !form.selectedDomains.includes('Industry');
                      const newDomains = isAdding
                        ? [...form.selectedDomains, 'Industry']
                        : form.selectedDomains.filter(d => d !== 'Industry');
                      const newIndustryConfigs = (isAdding && form.industryConfigs.length === 0)
                        ? [{ module_id: '', question_count: 0, difficulty_distribution: { easy: 0, medium: 0, hard: 0 }, scoring_scheme: { right: 1, wrong: 0 }, show_answers: true }]
                        : (isAdding ? form.industryConfigs : []);
                      updateForm({ selectedDomains: newDomains, industryConfigs: newIndustryConfigs });
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={form.selectedDomains.includes('Industry')}
                      onChange={() => { }}
                      disabled={isFieldDisabled}
                      style={{ width: '20px', height: '20px', cursor: isFieldDisabled ? 'not-allowed' : 'pointer', accentColor: 'var(--primary)', flexShrink: 0, opacity: isFieldDisabled ? 0.6 : 1 }}
                    />
                    <div className="is-human-panel-icon" style={{ background: "#FCE7F3", color: "#DB2777" }}>
                      <Database size={16} />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", width: "100%", justifyContent: "space-between" }}>
                      <h3 style={{ margin: 0 }}> Industry</h3>
                    </div>
                  </div>

                  {form.selectedDomains.includes('Industry') && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                      {form.industryConfigs.map((config, idx) => (
                        <div key={idx} className="is-form-row" style={{ alignItems: "center", background: "#ffffff", padding: "16px 20px", borderRadius: "12px", border: "1px solid #fbcfe8", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", display: "flex", flexWrap: "wrap", gap: "16px" }}>
                          {config.module_id && moduleStats[config.module_id] && (
                            <div style={{ flex: "1 1 100%", marginBottom: "12px" }}>
                              <div style={{ display: "inline-flex", fontSize: "14px", color: "#64748b", background: "#fdf2f8", padding: "10px 14px", borderRadius: "8px", gap: "20px", alignItems: "center" }}>
                                {moduleStats[config.module_id].loading ? (
                                  <span>Loading stats...</span>
                                ) : (
                                <>
                                  <span style={{ fontWeight: 600, color: "#334155" }}>Available Questions:</span>
                                  <span><span style={{ color: "#22c55e", fontWeight: 600 }}>Easy:</span> {moduleStats[config.module_id].easy}</span>
                                  <span><span style={{ color: "#eab308", fontWeight: 600 }}>Medium:</span> {moduleStats[config.module_id].medium}</span>
                                  <span><span style={{ color: "#ef4444", fontWeight: 600 }}>Hard:</span> {moduleStats[config.module_id].hard}</span>
                                  <span><span style={{ color: "#6366f1", fontWeight: 600 }}>Total:</span> {moduleStats[config.module_id].total}</span>
                                </>
                                )}
                              </div>
                            </div>
                          )}
                          <div className="is-form-group" style={{ flex: "1 1 200px", minWidth: "180px", marginBottom: 0 }}>
                            <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px", display: "block" }}>Module <span style={{ color: "#dc2626" }}>*</span></label>
                            <select
                              style={{ height: "36px", padding: "0 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", outline: "none", width: "100%", background: "#fff" }}
                              value={config.module_id}
                              disabled={isFieldDisabled}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val && form.industryConfigs.some((c, i) => i !== idx && c.module_id === val)) {
                                  setAlert({ show: true, title: "Error", message: "This module is already configured.", type: "error" });
                                  return;
                                }
                                const newConfigs = [...form.industryConfigs];
                                newConfigs[idx].module_id = val;
                                updateForm({ industryConfigs: newConfigs });
                                if (val) fetchModuleStats('industry', val);
                              }}
                            >
                              <option value="">Select Module</option>
                              {industryModules.map((m: any) => (
                                <option key={m.module_id || m.domain_id} value={m.module_id || m.domain_id}>{m.module_name || m.domain_name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="is-form-group" style={{ flex: "0 0 100px", width: "100px", marginBottom: 0 }}>
                            <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px", display: "block" }}>Questions</label>
                            <input
                              style={{ height: "36px", padding: "0 12px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", outline: "none", width: "100%", textAlign: "center", fontWeight: 700 }}
                              type="text"
                              inputMode="numeric"
                              value={config.question_count}
                              disabled={isFieldDisabled}
                              onChange={(e) => {
                                const raw = e.target.value.replace(/\D/g, "");
                                const stats = moduleStats[config.module_id];
                                const maxAllowed = stats && !stats.loading ? stats.total : 9999;
                                const newCount = Math.min(maxAllowed, Number(raw) || 0);
                                const newConfigs = [...form.industryConfigs];
                                newConfigs[idx].question_count = newCount;
                                newConfigs[idx].difficulty_distribution = autoDistributeDifficulty(newCount, stats && !stats.loading ? stats : undefined);
                                updateForm({ industryConfigs: newConfigs });
                              }}
                            />
                          </div>
                          <div className="is-form-group" style={{ flex: "0 0 auto", marginBottom: 0 }}>
                            <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px", display: "block" }}>Difficulty</label>
                            <DifficultyPopover 
                              disabled={isFieldDisabled} 
                              distribution={config.difficulty_distribution as any} 
                              maxQuestions={config.question_count} 
                              maxAvailable={moduleStats[config.module_id] && !moduleStats[config.module_id].loading ? { easy: moduleStats[config.module_id].easy, medium: moduleStats[config.module_id].medium, hard: moduleStats[config.module_id].hard } : undefined}
                              onChange={(dist: any) => {
                                const newConfigs = [...form.industryConfigs];
                                newConfigs[idx].difficulty_distribution = dist;
                                updateForm({ industryConfigs: newConfigs });
                              }} 
                            />
                          </div>
                          <div className="is-form-group" style={{ flex: "0 0 auto", marginBottom: 0 }}>
                            <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px", display: "block" }}>Scoring</label>
                            <ScoringPopover disabled={isFieldDisabled} scheme={config.scoring_scheme as any} onChange={(sch: any) => {
                              const newConfigs = [...form.industryConfigs];
                              newConfigs[idx].scoring_scheme = sch;
                              updateForm({ industryConfigs: newConfigs });
                            }} />
                          </div>
                          <div className="is-form-group" style={{ flex: "0 0 auto", marginBottom: 0 }}>
                            <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px", display: "block" }}>Show Answers</label>
                            <div style={{ display: "inline-flex", background: "#f1f5f9", padding: "3px", borderRadius: "8px", border: "1px solid #cbd5e1", height: "36px", boxSizing: "border-box", alignItems: "center" }}>
                              <button
                                type="button"
                                disabled={isFieldDisabled}
                                onClick={() => {
                                  if (isFieldDisabled) return;
                                  const newConfigs = [...form.industryConfigs];
                                  newConfigs[idx].show_answers = true;
                                  updateForm({ industryConfigs: newConfigs });
                                }}
                                style={{
                                  padding: "0 14px",
                                  height: "28px",
                                  borderRadius: "6px",
                                  fontSize: "13px",
                                  fontWeight: (config.show_answers ?? true) ? 700 : 500,
                                  cursor: isFieldDisabled ? "not-allowed" : "pointer",
                                  border: "none",
                                  background: (config.show_answers ?? true) ? "#10b981" : "transparent",
                                  color: (config.show_answers ?? true) ? "#ffffff" : "#64748b",
                                  transition: "0.2s",
                                  boxShadow: (config.show_answers ?? true) ? "0 1px 2px rgba(0,0,0,0.1)" : "none"
                                }}
                              >
                                Yes
                              </button>
                              <button
                                type="button"
                                disabled={isFieldDisabled}
                                onClick={() => {
                                  if (isFieldDisabled) return;
                                  const newConfigs = [...form.industryConfigs];
                                  newConfigs[idx].show_answers = false;
                                  updateForm({ industryConfigs: newConfigs });
                                }}
                                style={{
                                  padding: "0 14px",
                                  height: "28px",
                                  borderRadius: "6px",
                                  fontSize: "13px",
                                  fontWeight: !(config.show_answers ?? true) ? 700 : 500,
                                  cursor: isFieldDisabled ? "not-allowed" : "pointer",
                                  border: "none",
                                  background: !(config.show_answers ?? true) ? "#ef4444" : "transparent",
                                  color: !(config.show_answers ?? true) ? "#ffffff" : "#64748b",
                                  transition: "0.2s",
                                  boxShadow: !(config.show_answers ?? true) ? "0 1px 2px rgba(0,0,0,0.1)" : "none"
                                }}
                              >
                                No
                              </button>
                            </div>
                          </div>
                          {!isFieldDisabled && (
                            <div className="is-form-group" style={{ flex: "0 0 auto", marginLeft: "auto", alignSelf: "flex-end", marginBottom: 0 }}>
                              <button type="button" onClick={() => updateForm({ industryConfigs: form.industryConfigs.filter((_, i) => i !== idx) })} style={{ width: "36px", height: "36px", display: "flex", alignItems: "center", justifyContent: "center", color: "#ef4444", background: "#fef2f2", borderRadius: "8px", border: "1px solid #fca5a5", cursor: "pointer", transition: "0.2s" }} onMouseOver={(e) => e.currentTarget.style.background = "#fee2e2"} onMouseOut={(e) => e.currentTarget.style.background = "#fef2f2"}><X size={16} /></button>
                            </div>
                          )}
                        </div>
                      ))}
                      {!isFieldDisabled && (
                        <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '4px' }}>
                          <button type="button" onClick={() => updateForm({ industryConfigs: [...form.industryConfigs, { module_id: '', question_count: 0, difficulty_distribution: { easy: 0, medium: 0, hard: 0 }, scoring_scheme: { right: 1, wrong: 0 }, show_answers: true }] })} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 14px", background: "#fdf2f8", color: "#db2777", border: "1px solid #fbcfe8", borderRadius: "6px", fontSize: "14px", fontWeight: 600, cursor: "pointer", transition: "0.2s" }} onMouseOver={(e) => e.currentTarget.style.background = "#fce7f3"} onMouseOut={(e) => e.currentTarget.style.background = "#fdf2f8"}><Plus size={16} /> Add another module</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div
                className="is-form-actions"
                style={{
                  justifyContent: "flex-start",
                  gap: "12px",
                  paddingTop: "16px",
                  borderTop: "1px solid var(--border)",
                }}
              >
                <button
                  className="btn-ghost"
                  onClick={() => setStep(1)}
                  type="button"
                  style={{
                    width: "auto",
                    padding: "8px 16px",
                    height: "auto",
                    fontSize: "0.85rem",
                  }}
                >
                  Back
                </button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    if (step === 2) {
                      const jozunaQuestions = form.selectedDomains.includes('JOZUNA') ? (form.questionCount + form.descriptiveCount) : 0;
                      const programmingQuestions = form.selectedDomains.includes('Programming') ? form.programmingConfigs.reduce((acc, c) => acc + c.question_count, 0) : 0;
                      const industryQuestions = form.selectedDomains.includes('Industry') ? form.industryConfigs.reduce((acc, c) => acc + c.question_count, 0) : 0;
                      const total = jozunaQuestions + programmingQuestions + industryQuestions;
                      if (total <= 0) {
                        setAlert({
                          show: true,
                          title: "Error",
                          message: `Total questions must be greater than 0`,
                          type: "error",
                        });
                        return;
                      }
                    }
                    setStep(3);
                  }}
                  type="button"
                  style={{
                    width: "auto",
                    padding: "8px 16px",
                    height: "auto",
                    fontSize: "0.85rem",
                  }}
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Participants ── */}
          {step === 3 && (
            <div
              className="is-form-card"
              style={{
                padding: 0,
                border: "none",
                boxShadow: "none",
                background: "transparent",
              }}
            >
              <div className="lp-table-panel">
                <div
                  className="lp-table-panel-head"
                  style={{ padding: "14px 24px" }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <h2
                      style={{
                        margin: 0,
                        fontSize: "1rem",
                        fontWeight: 700,
                        color: "var(--text-heading)",
                      }}
                    >
                      Select Participants
                    </h2>
                    <span
                      className="lp-stat-badge active"
                      style={{ fontSize: "0.75rem", padding: "2px 8px" }}
                    >
                      <span className="lp-active-dot" />
                      {selectedUsers.length} Participant
                      {selectedUsers.length !== 1 ? "s" : ""}
                    </span>
                  </div>

                  <div
                    className="lp-search"
                    style={{
                      height: "36px",
                      width: "240px",
                      background: "var(--surface-alt)",
                    }}
                  >
                    <Search size={14} />
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={searchUser}
                      disabled={isFieldDisabled}
                      onChange={(e) => setSearchUser(e.target.value)}
                      style={{ fontSize: "0.8rem" }}
                    />
                    {searchUser && (
                      <button
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          color: "#94a3b8",
                          padding: 0,
                        }}
                        onClick={() => setSearchUser("")}
                      >
                        <svg
                          width="14"
                          height="14"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                <div className="lp-table-scroll-area">
                  {fetchingUsers ? (
                    <div
                      style={{
                        padding: "16px",
                        textAlign: "center",
                        color: "#64748b",
                        fontSize: "0.85rem",
                      }}
                    >
                      Loading users...
                    </div>
                  ) : users.length === 0 ? (
                    <div
                      className="is-empty"
                      style={{ padding: "40px 20px" }}
                    >
                      <Users
                        size={40}
                        color="#cbd5e1"
                        style={{ margin: "0 auto" }}
                      />
                      <h3
                        style={{
                          margin: "12px 0 4px",
                          fontSize: "1rem",
                        }}
                      >
                        No users found
                      </h3>
                      <p style={{ fontSize: "0.8rem" }}>
                        {searchUser
                          ? "Try adjusting your search"
                          : "No active participants available."}
                      </p>
                    </div>
                  ) : (
                    <>
                      <Table className="learner-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead
                              style={{ width: 40, paddingLeft: 20 }}
                            >
                              <input
                                type="checkbox"
                                className="lp-checkbox"
                                disabled={isFieldDisabled}
                                onChange={toggleAll}
                                checked={
                                  users.length > 0 &&
                                  users.every((u) =>
                                    selectedUsers.includes(u.candidate_id)
                                  )
                                }
                              />
                            </TableHead>
                            <TableHead>First Name</TableHead>
                            <TableHead>Last Name</TableHead>
                            <TableHead>Email Address</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {users.map((u) => (
                            <TableRow
                              key={u.candidate_id}
                              data-state={
                                selectedUsers.includes(u.candidate_id)
                                  ? "selected"
                                  : undefined
                              }
                            >
                              <TableCell style={{ paddingLeft: 20 }}>
                                <input
                                  type="checkbox"
                                  className="lp-checkbox"
                                  disabled={isFieldDisabled}
                                  checked={selectedUsers.includes(
                                    u.candidate_id
                                  )}
                                  onChange={() =>
                                    toggleUser(u.candidate_id)
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                <strong style={{ color: "#0f172a" }}>
                                  {u.first_name || "-"}
                                </strong>
                              </TableCell>
                              <TableCell>{u.last_name || "-"}</TableCell>
                              <TableCell>
                                <span style={{ color: "#475569" }}>
                                  {u.email_address}
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {hasMore && (
                        <div
                          style={{
                            padding: "12px",
                            textAlign: "center",
                          }}
                        >
                          <button
                            className="btn-ghost"
                            onClick={() =>
                              loadUsers(pageNumber + 1, false)
                            }
                            disabled={loadingMore}
                            style={{
                              width: "auto",
                              padding: "6px 16px",
                              height: "auto",
                              fontSize: "0.8rem",
                            }}
                          >
                            {loadingMore ? "Loading..." : "Load More"}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div
                className="is-form-actions"
                style={{
                  marginTop: "16px",
                  background: "var(--surface)",
                  padding: "16px 20px",
                  borderRadius: "var(--radius-xl)",
                  border: "1px solid var(--border)",
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: "12px",
                }}
              >
                <button
                  className="btn-ghost"
                  onClick={() => setStep(2)}
                  type="button"
                  style={{
                    width: "auto",
                    padding: "8px 16px",
                    height: "auto",
                    fontSize: "0.85rem",
                  }}
                >
                  Back
                </button>
                <button
                  className="btn-primary"
                  onClick={handleSubmit}
                  type="button"
                  disabled={saving || isClosed}
                  style={{
                    width: "auto",
                    padding: "8px 16px",
                    height: "auto",
                    fontSize: "0.85rem",
                  }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal open={closeAlert} onClose={() => setCloseAlert(false)} title="Close Assessment" width="420px">
        <div style={{ padding: '4px 0 16px', color: '#475569', fontSize: '14px', lineHeight: 1.5 }}>
          Are you sure you want to manually close the assessment <strong>{form.assessmentName}</strong>?
          Candidates will no longer be able to start or submit it.
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
          <button className="btn-cancel" onClick={() => setCloseAlert(false)} disabled={closing}>
            Cancel
          </button>
          <button className="btn-danger" style={{ backgroundColor: '#ef4444', borderColor: '#ef4444' }} onClick={handleCloseConfirm} disabled={closing}>
            {closing ? 'Closing...' : 'Close Assessment'}
          </button>
        </div>
      </Modal>

      <GlobeTimezoneModal
        isOpen={isGlobeModalOpen}
        onClose={() => setIsGlobeModalOpen(false)}
        onSelectTimezone={(tz) => updateForm({ timezone: tz })}
      />
    </motion.div>
  );
};

export default EditAssessmentPage;
